import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import QuickActions from '../Components/QuickActions';
import UserContext from '../Components/UserContext';
import { FaBoxOpen } from 'react-icons/fa';

const Market = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userCtx = useContext(UserContext);
  const session = userCtx?.session;
  const isLoggedIn = !!session?.id;
  const isStudentRoute = location.pathname.startsWith('/student/');
  const ordersRoute = isStudentRoute ? '/student/orders' : '/orders';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [categories, setCategories] = useState(['all']);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [globalSettings, setGlobalSettings] = useState({ isMarketplaceMockDataEnabled: true });
  const productsPerPage = 12;

  useEffect(() => {
    fetchGlobalSettings();
  }, []);

  const fetchGlobalSettings = async () => {
    try {
      const response = await HttpInterceptor.get('/api/admin/public/settings');
      if (response.data?.data) {
        setGlobalSettings(response.data.data);
      }
    } catch (error) {
      // Failed to fetch global settings
    }
  };

  useEffect(() => {
    fetchProducts();
    setCurrentPage(1);
  }, [selectedCategory, searchTerm, sortBy, globalSettings.isMarketplaceMockDataEnabled]);

  // Load cart from localStorage
  useEffect(() => {
    const loadCart = () => {
      try {
        const storedCart = JSON.parse(localStorage.getItem('marketplaceCart') || '[]');
        setCart(storedCart);
      } catch (error) {
        // Error loading cart
      }
    };
    loadCart();
    window.addEventListener('storage', loadCart);
    return () => window.removeEventListener('storage', loadCart);
  }, []);

  // Mock data for testing when resellers are not enrolled
  const getMockProducts = (page = 1) => {
    const mockProducts = [
      {
        _id: '507f1f77bcf86cd799439011',
        title: 'Premium Yoga Mat - Eco Friendly',
        description: 'High-quality, non-slip yoga mat made from eco-friendly materials. Perfect for all yoga practices.',
        price: 2499,
        discount: 15,
        stock: 25,
        category: 'yoga',
        image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400',
        resellerName: 'Wellness Store',
        resellerId: { _id: '507f1f77bcf86cd799439012', storeName: 'Wellness Store' },
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439021',
        title: 'Meditation Cushion Set',
        description: 'Comfortable meditation cushions with buckwheat filling. Includes zafu and zabuton for complete comfort.',
        price: 1899,
        discount: 10,
        stock: 15,
        category: 'meditation',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Zen Essentials',
        resellerId: { _id: '507f1f77bcf86cd799439022', storeName: 'Zen Essentials' },
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439031',
        title: 'Ayurvedic Herbal Tea Collection',
        description: 'Premium blend of Ayurvedic herbs for wellness and balance. Pack of 30 tea bags.',
        price: 599,
        discount: 0,
        stock: 50,
        category: 'herbal',
        image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=400',
        resellerName: 'Herbal Wellness',
        resellerId: { _id: '507f1f77bcf86cd799439032', storeName: 'Herbal Wellness' },
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439041',
        title: 'Essential Oil Diffuser - Aromatherapy',
        description: 'Ultrasonic essential oil diffuser with LED lights. Perfect for creating a calming atmosphere.',
        price: 1299,
        discount: 20,
        stock: 30,
        category: 'aromatherapy',
        image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400',
        resellerName: 'Aroma Bliss',
        resellerId: { _id: '507f1f77bcf86cd799439042', storeName: 'Aroma Bliss', address: { state: 'Delhi' } },
        hsnCode: '3304',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439051',
        title: 'Yoga Block Set (2 Blocks)',
        description: 'High-density foam yoga blocks for support and alignment. Set of 2 blocks in premium quality.',
        price: 899,
        discount: 5,
        stock: 40,
        category: 'yoga',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Yoga Pro',
        resellerId: { _id: '507f1f77bcf86cd799439052', storeName: 'Yoga Pro', address: { state: 'Maharashtra' } },
        hsnCode: '9506',
        createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439061',
        title: 'Crystal Healing Set',
        description: 'Beautiful collection of healing crystals including amethyst, rose quartz, and clear quartz.',
        price: 1999,
        discount: 12,
        stock: 20,
        category: 'wellness',
        image: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400',
        resellerName: 'Crystal Harmony',
        resellerId: { _id: '507f1f77bcf86cd799439062', storeName: 'Crystal Harmony', address: { state: 'Delhi' } },
        hsnCode: '7103',
        createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439071',
        title: 'Organic Turmeric Powder - 500g',
        description: 'Pure organic turmeric powder, sourced from organic farms. Rich in curcumin and antioxidants.',
        price: 399,
        discount: 0,
        stock: 100,
        category: 'nutrition',
        image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400',
        resellerName: 'Organic Life',
        resellerId: { _id: '507f1f77bcf86cd799439072', storeName: 'Organic Life' },
        createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439081',
        title: 'Yoga Strap with D-Ring',
        description: 'Premium cotton yoga strap with metal D-ring. 6 feet long, perfect for stretching and alignment.',
        price: 499,
        discount: 8,
        stock: 35,
        category: 'accessories',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Flex Yoga',
        resellerId: { _id: '507f1f77bcf86cd799439082', storeName: 'Flex Yoga' },
        createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439091',
        title: 'Bamboo Meditation Bowl Set',
        description: 'Handcrafted bamboo singing bowls for meditation and sound healing. Set includes striker and cushion.',
        price: 3499,
        discount: 18,
        stock: 12,
        category: 'meditation',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Sound Healing Co',
        resellerId: { _id: '507f1f77bcf86cd799439092', storeName: 'Sound Healing Co' },
        createdAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439101',
        title: 'Ayurvedic Face Care Kit',
        description: 'Complete Ayurvedic skincare set with natural ingredients. Includes cleanser, toner, and moisturizer.',
        price: 2299,
        discount: 25,
        stock: 18,
        category: 'skincare',
        image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=400',
        resellerName: 'AyurCare',
        resellerId: { _id: '507f1f77bcf86cd799439102', storeName: 'AyurCare' },
        createdAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439111',
        title: 'Pranayama Breathing Exercise Kit',
        description: 'Complete pranayama kit with breathing exercises guide and accessories for better respiratory health.',
        price: 1499,
        discount: 0,
        stock: 28,
        category: 'wellness',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Breath Wellness',
        resellerId: { _id: '507f1f77bcf86cd799439112', storeName: 'Breath Wellness' },
        createdAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439121',
        title: 'Yoga Wheel - Advanced Stretching',
        description: 'Professional yoga wheel for deep backbends and advanced stretching. Made from durable materials.',
        price: 2799,
        discount: 15,
        stock: 22,
        category: 'equipment',
        image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400',
        resellerName: 'Flex Pro',
        resellerId: { _id: '507f1f77bcf86cd799439122', storeName: 'Flex Pro' },
        createdAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439131',
        title: 'Resistance Bands Set - 5 Levels',
        description: 'Professional resistance bands for strength training and flexibility. Includes 5 different resistance levels.',
        price: 1299,
        discount: 10,
        stock: 45,
        category: 'fitness',
        image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400',
        resellerName: 'FitLife Store',
        resellerId: { _id: '507f1f77bcf86cd799439132', storeName: 'FitLife Store' },
        createdAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439141',
        title: 'Vitamin D3 + K2 Supplement - 60 Capsules',
        description: 'High-quality vitamin D3 and K2 supplement for bone health and immune support. 60 vegetarian capsules.',
        price: 899,
        discount: 15,
        stock: 60,
        category: 'supplements',
        image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?w=400',
        resellerName: 'Health Plus',
        resellerId: { _id: '507f1f77bcf86cd799439142', storeName: 'Health Plus' },
        createdAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439151',
        title: 'Yoga Pants - High Waist Leggings',
        description: 'Comfortable and stretchy yoga pants with high waist design. Perfect for yoga, fitness, and daily wear.',
        price: 1799,
        discount: 20,
        stock: 30,
        category: 'clothing',
        image: 'https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?w=400',
        resellerName: 'Active Wear Co',
        resellerId: { _id: '507f1f77bcf86cd799439152', storeName: 'Active Wear Co' },
        createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        _id: '507f1f77bcf86cd799439161',
        title: 'The Complete Guide to Yoga - Book',
        description: 'Comprehensive guide to yoga practice, philosophy, and benefits. Includes illustrations and step-by-step instructions.',
        price: 599,
        discount: 0,
        stock: 25,
        category: 'books',
        image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400',
        resellerName: 'Wellness Books',
        resellerId: { _id: '507f1f77bcf86cd799439162', storeName: 'Wellness Books' },
        createdAt: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
    const mockProductsWithFlag = mockProducts.map(p => ({ ...p, isMock: true }));

    // Filter by category
    let filtered = mockProductsWithFlag;
    if (selectedCategory !== 'all') {
      filtered = mockProductsWithFlag.filter(p => p.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.title.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower) ||
        p.resellerName.toLowerCase().includes(searchLower)
      );
    }

    // Sort products
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => {
        const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
        const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => {
        const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
        const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
        return priceB - priceA;
      });
    } else if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    // Pagination
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    return {
      products: paginated,
      hasMore: endIndex < filtered.length,
      categories: ['all', ...new Set(mockProducts.map(p => p.category).filter(Boolean))]
    };
  };

  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('limit', productsPerPage);
      params.append('skip', (page - 1) * productsPerPage);

      let productsData = [];
      let useMockData = false;

      try {
        const response = await HttpInterceptor.get(`/api/products?${params.toString()}`);

        // Handle different response formats
        if (Array.isArray(response.data)) {
          productsData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          productsData = response.data.data;
        } else if (response.data?.products && Array.isArray(response.data.products)) {
          productsData = response.data.products;
        } else {
          productsData = [];
        }

        // If no products returned, use mock data for testing
        if (!productsData || productsData.length === 0) {
          useMockData = true;
        } else {
          // Check if products array is actually empty after filtering
          const validProducts = productsData.filter(p => p && p._id);
          if (validProducts.length === 0) {
            useMockData = true;
          }
        }
      } catch (apiError) {
        // If API fails or returns error, use mock data
        useMockData = true;
      }

      // Use mock data if API returned no products or failed AND mock data is enabled
      if (useMockData && globalSettings.isMarketplaceMockDataEnabled) {
        const mockResult = getMockProducts(page);
        productsData = mockResult.products;
        setHasMore(mockResult.hasMore);

        // Extract unique categories from mock data
        setCategories(mockResult.categories);
      } else if (useMockData && !globalSettings.isMarketplaceMockDataEnabled) {
        // Mock data is disabled, so if API failed/returned nothing, just show empty
        productsData = [];
        setHasMore(false);
        setCategories(['all']);
      } else {
        // Check if there are more products
        setHasMore(productsData.length === productsPerPage);

        // Sort products
        if (sortBy === 'price-low') {
          productsData.sort((a, b) => {
            const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
            const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
            return priceA - priceB;
          });
        } else if (sortBy === 'price-high') {
          productsData.sort((a, b) => {
            const priceA = a.discount > 0 ? a.price * (1 - a.discount / 100) : a.price;
            const priceB = b.discount > 0 ? b.price * (1 - b.discount / 100) : b.price;
            return priceB - priceA;
          });
        } else if (sortBy === 'newest') {
          productsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }

        // Extract unique categories
        const uniqueCategories = ['all', ...new Set(productsData.map(p => p.category).filter(Boolean))];
        setCategories(uniqueCategories);
      }

      if (page === 1) {
        setProducts(productsData);
      } else {
        setProducts(prev => [...prev, ...productsData]);
      }
    } catch (error) {
      // Error fetching products
      if (globalSettings.isMarketplaceMockDataEnabled) {
        const mockResult = getMockProducts(page);
        if (page === 1) {
          setProducts(mockResult.products);
        } else {
          setProducts(prev => [...prev, ...mockResult.products]);
        }
        setHasMore(mockResult.hasMore);
        setCategories(mockResult.categories);
      } else {
        setProducts([]);
        setHasMore(false);
        setCategories(['all']);
      }
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.productId === product._id);

    let updatedCart;
    if (existingItem) {
      if (product.stock <= existingItem.quantity) {
        toast.error('Insufficient stock');
        return;
      }
      updatedCart = cart.map(item =>
        item.productId === product._id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      if (product.stock < 1) {
        toast.error('Product out of stock');
        return;
      }
      updatedCart = [...cart, {
        productId: product._id,
        title: product.title,
        price: product.price,
        discount: product.discount,
        image: product.image,
        gstRate: product.gstRate || 18,
        hsnCode: product.hsnCode || '',
        resellerState: product.resellerId?.address?.state || product.resellerState || 'platform',
        quantity: 1,
        resellerId: (product.resellerId && product.resellerId._id) || product.resellerId
      }];
    }
    setCart(updatedCart);
    localStorage.setItem('marketplaceCart', JSON.stringify(updatedCart));
    toast.success('Added to cart');
    window.dispatchEvent(new Event('storage'));
  };

  const removeFromCart = (productId) => {
    const updatedCart = cart.filter(item => item.productId !== productId);
    setCart(updatedCart);
    localStorage.setItem('marketplaceCart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('storage'));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    const updatedCart = cart.map(item =>
      item.productId === productId
        ? { ...item, quantity }
        : item
    );
    setCart(updatedCart);
    localStorage.setItem('marketplaceCart', JSON.stringify(updatedCart));
    window.dispatchEvent(new Event('storage'));
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchProducts(nextPage);
    }
  };

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const loadMoreTrigger = document.getElementById('load-more-trigger');
    if (loadMoreTrigger) {
      observer.observe(loadMoreTrigger);
    }

    return () => {
      if (loadMoreTrigger) {
        observer.unobserve(loadMoreTrigger);
      }
    };
  }, [hasMore, loading, currentPage]);

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      const discount = (itemTotal * item.discount) / 100;
      return total + itemTotal - discount;
    }, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Store cart in localStorage
    localStorage.setItem('marketplaceCart', JSON.stringify(cart));

    // Navigate to appropriate checkout page
    if (isStudentRoute) {
      navigate('/student/checkout');
    } else {
      navigate('/checkout');
    }
  };


  const calculateDiscountedPrice = (price, discount) => {
    return price * (1 - discount / 100);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="relative mb-8 flex flex-col items-center">
          {/* Top Actions: Cart & My Orders */}
          <div className="w-full flex justify-center md:justify-end md:absolute md:top-0 md:right-0 mb-6 md:mb-0 gap-2 md:gap-3">
            {/* Cart Button */}
            <button
              onClick={() => setShowCart(true)}
              className="relative flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md text-sm md:text-base"
              aria-label={`Shopping cart with ${cart.reduce((sum, item) => sum + item.quantity, 0)} items`}
            >
              <span className="text-lg leading-none">🛒</span>
              <span className="font-medium hidden sm:inline">Cart</span>
              <span className="font-medium sm:hidden">Cart</span>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold border-2 border-white">
                  {cart.reduce((sum, item) => sum + item.quantity, 0) > 99 ? '99+' : cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              )}
            </button>

            {/* My Orders */}
            {isLoggedIn && isStudentRoute && (
              <Link
                to={ordersRoute}
                className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md text-sm md:text-base"
              >
                <FaBoxOpen className="text-base md:text-lg" />
                <span className="font-medium hidden sm:inline">My Orders</span>
                <span className="font-medium sm:hidden">My Orders</span>
              </Link>
            )}
          </div>

          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
              Marketplace
            </h1>
            <p className="text-gray-600 text-lg">Discover wellness products from trusted resellers</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            aria-label="Search products"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            aria-label="Sort products"
          >
            <option value="newest">Newest First</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
          </select>
        </div>

        {/* Category Filter */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full whitespace-nowrap ${selectedCategory === category
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>



        {/* Mobile Welcome Banner */}
        <div className="mb-6 md:hidden bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 text-white">
          <h2 className="text-xl font-bold mb-2">Welcome to Marketplace</h2>
          <p className="text-sm text-purple-100">Discover wellness products from trusted resellers</p>
        </div>

        {/* Products Grid with Lazy Loading */}
        {loading && currentPage === 1 ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl mb-4">No products found</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="text-purple-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {products.map(product => {
                const discountedPrice = calculateDiscountedPrice(product.price, product.discount);
                return (
                  <div
                    key={product._id}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                  >
                    <Link
                      to={`/product/${product._id}`}
                      className="relative h-48 bg-gray-200 cursor-pointer block"
                    >
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      {!product.image && (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                      {product.discount > 0 && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">
                          -{product.discount}%
                        </div>
                      )}
                    </Link>
                    <div className="p-4">
                      <Link
                        to={`/product/${product._id}`}
                        className="font-bold text-lg mb-2 cursor-pointer hover:text-purple-600 line-clamp-2 block"
                      >
                        {product.title}
                      </Link>
                      {product.resellerId && (
                        <p className="text-xs text-gray-500 mb-2">
                          by {product.resellerName || product.resellerId?.storeName || 'Reseller'}
                        </p>
                      )}
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {product.description || 'No description available'}
                      </p>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          {product.discount > 0 ? (
                            <div>
                              <span className="text-2xl font-bold text-purple-600">
                                ₹{discountedPrice.toFixed(2)}
                              </span>
                              <span className="text-gray-400 line-through ml-2">
                                ₹{product.price}
                              </span>
                            </div>
                          ) : (
                            <span className="text-2xl font-bold text-purple-600">
                              ₹{product.price}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {product.stock > 0 
                            ? (product.isMock ? 'In Stock (mock data)' : `In Stock (${product.stock})`) 
                            : 'Out of Stock'}
                        </span>
                        <button
                          onClick={() => addToCart(product)}
                          disabled={product.stock === 0}
                          className={`px-4 py-2 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${product.stock > 0
                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          aria-label={`Add ${product.title} to cart`}
                        >
                          Add to Cart
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Trigger for Infinite Scroll */}
            {hasMore && (
              <div id="load-more-trigger" className="flex justify-center py-8">
                {loading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                ) : (
                  <button
                    onClick={loadMore}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition-colors"
                    aria-label="Load more products"
                  >
                    Load More Products
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Floating Cart Button for Mobile */}
      <div className="md:hidden fixed bottom-9 left-4 z-[90]">
        <button
          onClick={() => setShowCart(true)}
          className="relative flex items-center justify-center bg-purple-600 text-white rounded-full w-14 h-14 shadow-2xl hover:bg-purple-700 transition-transform active:scale-95 border-2 border-white"
          aria-label={`Shopping cart with ${cart.reduce((sum, item) => sum + item.quantity, 0)} items`}
        >
          <span className="text-2xl">🛒</span>
          {cart.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold border-2 border-white shadow-sm">
              {cart.reduce((sum, item) => sum + item.quantity, 0) > 99 ? '99+' : cart.reduce((sum, item) => sum + item.quantity, 0)}
            </span>
          )}
        </button>
      </div>

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Shopping Cart</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              {cart.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Your cart is empty</p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map(item => (
                      <div key={item.productId} className="flex items-center gap-4 border-b pb-4">
                        <img
                          src={item.image || '/placeholder.png'}
                          alt={item.title}
                          className="w-20 h-20 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold">{item.title}</h4>
                          <p className="text-gray-600">
                            ₹{calculateDiscountedPrice(item.price, item.discount).toFixed(2)} × {item.quantity}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            Includes {item.gstRate || 18}% GST
                            {item.hsnCode && ` • HSN: ${item.hsnCode}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartQuantity(item.productId, item.quantity - 1)}
                            className="px-2 py-1 border rounded"
                          >
                            -
                          </button>
                          <span>{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.productId, item.quantity + 1)}
                            className="px-2 py-1 border rounded"
                          >
                            +
                          </button>
                          <button
                            onClick={() => removeFromCart(item.productId)}
                            className="ml-4 text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    {(() => {
                      const exactTotal = calculateTotal();
                      const roundedTotal = Math.round(exactTotal);
                      const roundOffAmount = roundedTotal - exactTotal;

                      let baseTotal = 0;
                      const gstBreakdown = {};

                      cart.forEach(item => {
                        const itemTotalFixed = calculateDiscountedPrice(item.price, item.discount) * item.quantity;
                        const gstRate = item.gstRate || 18;
                        const gstRateMultiplier = gstRate / 100;
                        const itemBase = itemTotalFixed / (1 + gstRateMultiplier);
                        baseTotal += itemBase;
                        const itemGst = itemTotalFixed - itemBase;

                        if (!gstBreakdown[gstRate]) {
                          gstBreakdown[gstRate] = { totalGst: 0, cgst: 0, sgst: 0 };
                        }
                        gstBreakdown[gstRate].totalGst += itemGst;
                      });

                      return (
                        <>
                          <div className="flex justify-between text-gray-600 mb-2">
                            <span>Subtotal (Excl. Tax):</span>
                            <span>₹{baseTotal.toFixed(2)}</span>
                          </div>
                          {Object.keys(gstBreakdown).sort((a, b) => a - b).map(rate => {
                            const rateGst = gstBreakdown[rate];
                            const cgst = rateGst.totalGst / 2;
                            const sgst = rateGst.totalGst / 2;
                            return (
                              <React.Fragment key={rate}>
                                <div className="flex justify-between text-gray-600 text-sm mb-1">
                                  <span>CGST ({rate / 2}%):</span>
                                  <span>₹{cgst.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-gray-600 text-sm mb-2">
                                  <span>SGST ({rate / 2}%):</span>
                                  <span>₹{sgst.toFixed(2)}</span>
                                </div>
                              </React.Fragment>
                            );
                          })}
                          {roundOffAmount !== 0 && (
                            <div className="flex justify-between text-gray-500 text-sm mb-2">
                              <span>Round off:</span>
                              <span>{roundOffAmount > 0 ? '+' : ''}₹{roundOffAmount.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-gray-100 mb-4">
                      <span>Total:</span>
                      <span>₹{Math.round(calculateTotal()).toFixed(2)}</span>
                    </div>
                    <button
                      onClick={handleCheckout}
                      className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700"
                    >
                      Proceed to Checkout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <QuickActions />
    </div>
  );
};

export default Market;
